// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  amber,
  blue,
  blueGrey,
  brown,
  deepOrange,
  deepPurple,
  indigo,
  pink,
  purple,
  teal,
} from "@mui/material/colors";

// Snack messages
export const SnackMessage = {
  success: {
    addJR: "JR added successfully",
    updateJR: "JR status updated successfully",
    updateVacancy: "Vacancy updated successfully",
    vacancyPublished: "Vacancy published successfully",
    updateCandidate: "Candidate updated successfully",
    addJD: "JD added successfully",
    updateJD: "Job description updated successfully",
    addJDComment: "Comment added successfully",
    deleteJDComment: "Comment deleted successfully",
    deleteJR: "JR deleted successfully",
    deleteCandidate: "Candidate deleted successfully",
    addVacancy: "Vacancy added successfully",
    deleteVacancy: "Vacancy deleted successfully",
    closeVacancy: "Vacancy closed successfully",
    addCandidate: "Candidate added successfully",
    editCandidate: "Candidate edited successfully",
    addInterview: "Interview added successfully",
    editInterview: "Interview edited successfully",
    deleteInterview: "Interview deleted successfully",
    addScore: "Score added successfully",
    sendFeedbackReminder: "Feedback reminder sent successfully",
    updateScore: "Score updated successfully",
    sendJdNotificationEmail: "JD notification email sent successfully",
    sendJdChangeNotification: "Notification email sent successfully",
    sendOfferLetter: "Offer letter sent and archived successfully",
    sendBulkOfferLetters: "All selected offer letters sent successfully",
    vacancyPutOnHold: "Vacancy put on hold successfully",
    vacancyRepublished: "Vacancy re-published successfully",
    addParticipant: "Participant added successfully",
    deleteApplicationReviewer: "Application reviewer(s) removed successfully",
  },
  error: {
    addJR: "Error while adding JR",
    updateJR: "Error while updating JR",
    updateVacancy: "Error while updating vacancy",
    updateCandidate: "Error while updating candidate",
    deleteCandidate: "Error while deleting candidate",
    addJD: "Error while adding JD",
    updateJD: "Failed to update job description",
    addJDComment: "Failed to add comment",
    deleteJDComment: "Failed to delete comment",
    deleteJR: "Error while deleting JR",
    addVacancy: "Error while adding vacancy",
    deleteVacancy: "Error while deleting vacancy",
    closeVacancy: "Error while closing vacancy",
    addCandidate: "Error while adding candidate",
    getVacancyId: "Error while getting vacancy ID",
    editCandidate: "Error while editing candidate data",
    addInterview: "Error while adding interview",
    getInterview: "Error while getting interview",
    getCandidates: "Error while getting candidates",
    getCandidateHistory: "Error while getting candidate history",
    editInterview: "Error while editing interview",
    deleteInterview: "Error while deleting interview",
    getInterviewFeedbacks: "Error while fetching interview feedbacks.",
    addScore: "Error while adding score",
    sendFeedbackReminder: "Error while sending feedback reminder",
    updateScore: "Error while updating score",
    duplicateCandidate: "Candidate has already applied for this vacancy",
    sendJdNotificationEmail: "Failed to send JD notification email",
    sendJdChangeNotification: "Failed to send notification email",
    sendOfferLetter: "Failed to send offer letter",
    fetchParticipants: "Failed to retrieve participants",
    addParticipant: "Failed to add participant",
    deleteApplicationReviewer: "Failed to remove application reviewer(s)",
  },
  warning: {
    addJR: "",
    addVacancy: "",
  },
};

export const UIMessages = {
  loading: {
    landingPageLoading: "Loading",
  },
};

// Messages
export const requiredField = "Required";
export const timeInvalidErrorMessage =
  "Start time should be earlier than end time.";
export const Loading = "Loading";
export const SomethingWentWrong = "Something went wrong";

export const POSITION_STATUS_LIST: string[] = [
  "New hire",
  "Replacement",
  "Inter-company transfer",
];

export const POSITION_TYPE_LIST: string[] = [
  "Permanent",
  "Internship",
  "Consultancy",
];

export const CANDIDATE_SOURCE_LIST: string[] = [
  "Internal",
  "LinkedIn",
  "Career Site",
  "Ex-Intern",
  "Career Fair",
  "Referral",
  "External Headhunter",
];
export const INTERVIEW_OVERALL_DECISION: string[] = [
  "Not Recommended",
  "Recommended",
  "Highly Recommended",
];
export const JR_FORM_STEPS: number = 4;
export const INTERVIEW_MODALITIES: string[] = ["Virtual", "Physical"];
export const SCORE_CARD_CLASSIFICATION_LIST: string[] = [
  "Unsatisfied",
  "Needs Improvements",
  "Satisfactory",
  "Good",
  "Exceptional",
];
export const VACANCY_VISIBILITY: string[] = [
  "Without Listing",
  "All",
  "External Only",
  "Internal Only",
];
export const MODE_OF_WORK: string[] = ["Office", "Remote", "Hybrid"];

export enum JobRequisitionStatus {
  CREATED = "CREATED",
  IN_REVIEW = "IN_REVIEW",
  APPROVED = "APPROVED",
  VACANCY_CREATED = "VACANCY_CREATED",
  REJECTED = "REJECTED",
  DELETED = "DELETED",
}
export enum InterviewOverallDecision {
  NOT_RECOMMENDED = "Not Recommended",
  RECOMMENDED = "Recommended",
  HIGHLY_RECOMMENDED = "Highly Recommended",
}
export enum VacancyVisibility {
  WITHOUT_LISTING = "Without Listing",
  ALL = "All",
  EXTERNAL_ONLY = "External Only",
  INTERNAL_ONLY = "Internal Only",
}
export enum VacancyStatus {
  CREATED = "CREATED",
  IN_REVIEW = "IN_REVIEW",
  REQUEST_CHANGE = "REQUEST_CHANGE",
  PUBLISHED = "PUBLISHED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  CLOSED = "CLOSED",
  DELETED = "DELETED",
  ON_HOLD = "ON_HOLD",
}
export enum CandidateStatus {
  CREATED = "CREATED",
  SHORTLISTED = "SHORTLISTED",
  NOT_SHORTLISTED = "NOT_SHORTLISTED",
  INTERVIEW_IN_PROGRESS = "INTERVIEW_IN_PROGRESS",
  INTERVIEW_COMPLETED = "INTERVIEW_COMPLETED",
  HIRED = "HIRED",
  REJECTED = "REJECTED",
  DELETED = "DELETED",
  VACANCY_CLOSED = "VACANCY_CLOSED",
}
export enum InterviewStatus {
  SCHEDULED = "SCHEDULED",
  COMPLETED = "COMPLETED",
  WITHDRAWN = "WITHDRAWN",
}
export enum Genders {
  MALE = "Male",
  FEMALE = "Female",
  PREFER_NOT_TO_SAY = "Prefer not to say",
}

export const avatarBgColors = [
  deepOrange,
  deepPurple,
  amber,
  brown,
  indigo,
  blueGrey,
  pink,
  purple,
  teal,
  blue,
];

export const defaultPaginationValues = {
  limit: 5,
  offset: 0,
};

export const ITEM_TYPE = "candidate";

export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [10, 25, 50];

export enum RejectionType {
  NOT_SHORTLISTED = "Not_Shortlisted",
  REJECT_INITIAL_INTERVIEWS = "Reject_Initial_Interviews",
  REJECT_SECOND_ROUND_INTERVIEWS = "Reject_Second_Round_Interviews",
  REJECT_FINAL_INTERVIEWS = "Reject_Final_Interviews",
}

export enum InterviewType {
  NORMAL = "Normal",
  FINAL = "Final",
  CASE_STUDY = "Case Study",
}
